<template>
  <b-row>
    <b-col cols="12">
      <b-card>
        <template #header>
          <div class="d-flex justify-content-between align-items-center w-100">
            <h4 class="mb-0">
              <feather-icon
                icon="SettingsIcon"
                class="mr-50 text-primary"
              />
              {{ isNew ? 'Novo Layout de Importação' : 'Configurações do Layout' }}
            </h4>
            <b-badge
              v-if="!isNew"
              variant="light-primary"
              pill
            >
              ID: {{ layout.id }}
            </b-badge>
          </div>
        </template>

        <!-- Seção de Identificação (Destaque) -->
        <div class="header-config-section bg-light-section p-2 rounded mb-3 border">
          <b-row>
            <b-col
              cols="12"
              md="7"
            >
              <b-form-group
                label="Nome do Layout"
                label-for="layout-nome"
                label-class="font-weight-bold text-dark"
              >
                <b-input-group size="lg">
                  <template #prepend>
                    <b-input-group-text class="bg-white border-right-0">
                      <feather-icon
                        icon="TypeIcon"
                        size="18"
                        class="text-primary"
                      />
                    </b-input-group-text>
                  </template>
                  <b-form-input
                    id="layout-nome"
                    v-model="layout.nome"
                    placeholder="Ex: Conciliação Bancária - Banco do Brasil"
                    class="border-left-0 pl-0 font-weight-bold"
                  />
                </b-input-group>
                <small class="text-muted mt-25 d-block">Dê um nome claro para identificar este layout futuramente.</small>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="5"
            >
              <b-form-group
                label="Tipo do Arquivo de Origem"
                label-for="layout-tipo"
                label-class="font-weight-bold text-dark"
              >
                <v-select
                  id="layout-tipo"
                  v-model="layout.tipoArquivo"
                  :options="fileTypes"
                  placeholder="Selecione o formato..."
                  class="custom-v-select select-lg"
                />
                <small class="text-muted mt-25 d-block">Informe se o arquivo é Excel, CSV ou TXT.</small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                label="Pular Linhas (Cabeçalho)"
                label-for="layout-skip"
                label-class="font-weight-bold small"
              >
                <b-input-group>
                  <template #prepend>
                    <b-input-group-text><feather-icon
                      icon="SkipForwardIcon"
                      size="14"
                    /></b-input-group-text>
                  </template>
                  <b-form-input
                    id="layout-skip"
                    v-model="layout.linhasPular"
                    type="number"
                    placeholder="0"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                label="Tipo Financeiro (Vínculo)"
                label-for="layout-finan"
                label-class="font-weight-bold small"
              >
                <b-input-group>
                  <template #prepend>
                    <b-input-group-text><feather-icon
                      icon="DollarSignIcon"
                      size="14"
                    /></b-input-group-text>
                  </template>
                  <b-form-input
                    id="layout-finan-id"
                    v-model="layout.tipoPagRecId"
                    type="number"
                    placeholder="ID (Ex: 6)"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                label="Atualizar valor do lançamento?"
                label-for="layout-prevalecer"
                label-class="font-weight-bold small"
              >
                <b-form-checkbox
                  id="layout-prevalecer"
                  v-model="layout.prevalecerValorArquivo"
                  switch
                  class="custom-control-success mt-50"
                >
                  <span
                    class="small font-weight-bold"
                    :class="layout.prevalecerValorArquivo ? 'text-success' : 'text-secondary'"
                  >
                    {{ layout.prevalecerValorArquivo ? 'Sim (Arquivo Prevalece)' : 'Não (Mantém Original)' }}
                  </span>
                </b-form-checkbox>
                <small class="text-muted d-block mt-25">Se ativado, altera o valor no banco para bater com o arquivo na quitação.</small>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="divider divider-primary mb-2">
          <div class="divider-text">
            Mapeamento de Dados
          </div>
        </div>

        <!-- Toolbar de Ações do Mapeamento -->
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div class="text-muted small">
            <feather-icon
              icon="InfoIcon"
              size="14"
              class="mr-25"
            />
            Defina as regras de extração para cada campo do arquivo.
          </div>
          <b-button
            variant="success"
            size="sm"
            @click="addRow"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            Adicionar Campo
          </b-button>
        </div>

        <!-- Grid Editável -->
        <div class="mapping-table-container border rounded overflow-hidden mb-2">
          <table class="table table-borderless table-striped mb-0">
            <thead class="bg-light">
              <tr>
                <th class="py-1">
                  Nome no Arquivo
                </th>
                <th
                  v-if="layout.tipoArquivo === 'TXT_POSICIONAL'"
                  class="py-1"
                >
                  Posição
                </th>
                <th
                  v-if="layout.tipoArquivo === 'TXT_POSICIONAL'"
                  class="py-1"
                >
                  Tamanho
                </th>
                <th class="py-1">
                  Tipo de Dado
                </th>
                <th class="py-1">
                  Destino no Sistema
                </th>
                <th class="py-1">
                  Formato Extra
                </th>
                <th
                  class="py-1 text-center"
                  style="width: 50px"
                />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(m, index) in layout.mappings"
                :key="index"
                class="align-middle"
              >
                <td>
                  <b-form-input
                    v-model="m.campoDestino"
                    size="sm"
                    placeholder="Ex: VALOR_BOLETO"
                    class="bg-white"
                  />
                </td>
                <td v-if="layout.tipoArquivo === 'TXT_POSICIONAL'">
                  <b-form-input
                    v-model="m.posicaoInicial"
                    type="number"
                    size="sm"
                    class="text-center"
                  />
                </td>
                <td v-if="layout.tipoArquivo === 'TXT_POSICIONAL'">
                  <b-form-input
                    v-model="m.tamanho"
                    type="number"
                    size="sm"
                    class="text-center"
                  />
                </td>
                <td>
                  <v-select
                    v-model="m.tipoDado"
                    :options="dataTypes"
                    size="sm"
                    :clearable="false"
                    class="v-select-sm"
                  />
                </td>
                <td>
                  <v-select
                    v-model="m.usoCampo"
                    :options="useFields"
                    :reduce="o => o.value"
                    label="text"
                    size="sm"
                    placeholder="Escolha..."
                    class="v-select-sm"
                  />
                </td>
                <td>
                  <b-form-input
                    v-model="m.formatoExtra"
                    size="sm"
                    placeholder="Opcional"
                  />
                </td>
                <td class="text-center">
                  <b-button
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon rounded-circle"
                    @click="removeRow(index)"
                  >
                    <feather-icon
                      icon="Trash2Icon"
                      size="14"
                    />
                  </b-button>
                </td>
              </tr>
              <tr v-if="layout.mappings.length === 0">
                <td
                  colspan="7"
                  class="text-center py-2 text-muted italic"
                >
                  Nenhum mapeamento definido. Clique em "Adicionar Campo" para começar.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Live Preview / Data Inspector -->
        <div class="data-inspector bg-dark text-light p-1 rounded-lg mb-2 shadow-lg overflow-hidden position-relative">
          <div class="d-flex align-items-center mb-1 border-bottom border-secondary pb-75">
            <feather-icon
              icon="TerminalIcon"
              size="18"
              class="mr-50 text-success"
            />
            <span class="font-weight-bolder small text-uppercase tracking-wider">Inspetor de Dados (Live Preview)</span>
          </div>

          <b-form-group label-class="text-secondary small mb-50">
            <b-form-input
              v-model="exampleLine"
              placeholder="Cole uma linha bruta do arquivo aqui para testar..."
              size="sm"
              class="bg-transparent border-secondary text-white font-monospace mb-1"
            />
          </b-form-group>

          <div
            v-if="exampleLine"
            class="d-flex flex-wrap gap-2"
          >
            <div
              v-for="(m, i) in validMappings"
              :key="i"
              class="inspector-chip mr-1 mb-1 p-50 border border-secondary rounded bg-black-25"
            >
              <div class="d-flex align-items-center justify-content-between mb-25">
                <span
                  class="badge badge-light-secondary badge-pill py-0 px-50"
                  style="font-size: 10px"
                >{{ m.campoDestino || 'col'+i }}</span>
              </div>
              <div
                class="font-weight-bold text-success font-monospace"
                style="font-size: 13px"
              >
                {{ extractPreview(m) || 'null' }}
              </div>
            </div>
            <div
              v-if="validMappings.length === 0"
              class="text-secondary small italic"
            >
              Aguardando mapeamento de posição/tamanho para extrair dados...
            </div>
          </div>
        </div>

        <!-- Rodapé com Ações -->
        <div class="d-flex justify-content-end align-items-center mt-2 pt-1 border-top">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="$router.back()"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              class="mr-50"
            />
            Voltar
          </b-button>
          <b-button
            variant="primary"
            :disabled="loading"
            size="lg"
            class="px-2"
            @click="handleSave"
          >
            <feather-icon
              icon="CheckIcon"
              class="mr-50"
            />
            Finalizar e Salvar
          </b-button>
        </div>

        <b-overlay
          :show="loading"
          no-wrap
          rounded="sm"
          variant="white"
          opacity="0.8"
        />
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow, BCol, BCard, BFormGroup, BFormInput, BButton, BOverlay, BInputGroup, BInputGroupText, BBadge, BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { getLayout, saveLayout } from '@/api/financialModule/dynamicImport'
import mixinsGlobal from '@/mixins'

export default {
  components: {
    BRow, BCol, BCard, BFormGroup, BFormInput, BButton, BOverlay, vSelect, BFormCheckbox,
  },
  mixins: [mixinsGlobal],
  data() {
    return {
      isNew: true,
      loading: false,
      exampleLine: '',
      layout: {
        nome: '',
        tipoArquivo: 'TXT_POSICIONAL',
        linhasPular: 0,
        tipoPagRecId: null,
        prevalecerValorArquivo: false,
        mappings: [],
      },
      fileTypes: ['TXT_POSICIONAL', 'EXCEL', 'CSV', 'OFX'],
      dataTypes: ['TEXTO', 'DECIMAL', 'DECIMAL_POS', 'DATA', 'DATE_YYYYMM'],
      useFields: [
        { text: 'Sócio: CPF', value: 'IDENTIFICADOR_SOCIO_CPF' },
        { text: 'Sócio: Matrícula', value: 'IDENTIFICADOR_SOCIO_MATRICULA' },
        { text: 'Valor do Pagamento', value: 'VALOR_PAGAMENTO' },
        { text: 'Data de Competência', value: 'DATA_COMPETENCIA' },
        { text: 'Observação', value: 'OBSERVACAO' },
      ],
    }
  },
  computed: {
    validMappings() {
      return this.layout.mappings.filter(m => m.posicaoInicial !== null && m.tamanho > 0)
    },
  },
  mounted() {
    this.loadLayout()
  },
  methods: {
    async loadLayout() {
      const { id } = this.$route.params
      if (!id || id === 'new') return

      this.isNew = false
      this.loading = true
      try {
        const response = await getLayout(id)
        this.layout = response.data
      } catch (error) {
        this.MensagemError('Erro ao carregar layout')
      } finally {
        this.loading = false
      }
    },
    addRow() {
      this.layout.mappings.push({
        campoDestino: '',
        posicaoInicial: 0,
        tamanho: 0,
        tipoDado: 'TEXTO',
        usoCampo: null,
        formatoExtra: '',
      })
    },
    removeRow(index) {
      this.layout.mappings.splice(index, 1)
    },
    extractPreview(mapping) {
      if (!this.exampleLine) return ''
      try {
        const start = parseInt(mapping.posicaoInicial)
        const size = parseInt(mapping.tamanho)
        return this.exampleLine.substring(start, start + size)
      } catch (e) {
        return 'ERR'
      }
    },
    async handleSave() {
      if (!this.layout.nome) {
        this.MensagemError('O nome do layout é obrigatório.')
        return
      }

      this.loading = true
      try {
        // Map campoDestino if not set
        this.layout.mappings.forEach((m, i) => {
          if (!m.campoDestino) m.campoDestino = `col${i}`
        })

        await saveLayout(this.layout)
        this.showMessageSuccess('Sucesso', 'Layout salvo com sucesso.')
        this.$router.push({ name: 'importLayoutSettings' })
      } catch (error) {
        this.MensagemError('Erro ao salvar layout.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.bg-light-section {
  background-color: #fcfcfc;
}

.custom-v-select {
  &.select-lg {
    .vs__dropdown-toggle {
      min-height: 48px;
      font-size: 1.1rem;
    }
    .vs__selected {
      margin-top: 8px;
    }
  }
  .vs__dropdown-toggle {
    background-color: #fff;
    border: 1px solid #d8d6de;
  }
}

.mapping-table-container {
  background: #f8f8f8;
  .table {
    th {
      font-size: 0.85rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      font-weight: 600;
    }
  }
}

.data-inspector {
  background-color: #1e1e1e !important;
  font-family: 'Courier New', Courier, monospace;

  .form-control {
    &:focus {
      background-color: rgba(255, 255, 255, 0.05);
      border-color: #28c76f;
    }
  }

  .inspector-chip {
    transition: all 0.3s ease;
    border-color: #444 !important;

    &:hover {
      border-color: #28c76f !important;
      background-color: rgba(40, 199, 111, 0.1);
    }
  }
}

.bg-black-25 {
  background-color: rgba(0, 0, 0, 0.25);
}

.v-select-sm {
  .vs__dropdown-toggle {
    padding: 0;
    min-height: 31px;
    font-size: 0.9rem;
  }
  .vs__selected-options {
    padding: 0 4px;
  }
  .vs__actions {
    padding: 2px 4px 0 2px;
  }
}

.gap-2 {
  gap: 0.5rem;
}
</style>
